import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { BookOpen } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import {
  AccountingHospitalProvider,
  useAccountingHospitalContext,
} from "./AccountingHospitalContext";

const tabs = [
  { to: ".", end: true, label: "Dashboard" },
  { to: "vouchers", end: true, label: "Vouchers" },
  { to: "vouchers/new", label: "New journal" },
  { to: "trial-balance", label: "Trial balance" },
  { to: "general-ledger", label: "General ledger" },
  { to: "ops-reconciliation", label: "Ops reconciliation" },
  { to: "ageing", label: "AR / AP ageing" },
  { to: "tax", label: "GST / TDS" },
  { to: "bank-pharmacy", label: "Bank / Pharmacy" },
  { to: "statements", label: "Statements / Assets" },
  { to: "cfo", label: "CFO KPIs" },
  { to: "chart-of-accounts", label: "Chart of accounts" },
  { to: "periods", label: "FY / Periods" },
  { to: "opening-balances", label: "Opening balances" },
];

function AccountingShell() {
  const location = useLocation();
  const inCaPortal = location.pathname.startsWith("/chartered-accountant");
  const {
    hospitalId,
    setHospitalId,
    hospitalOptions,
    needsHospitalPicker,
    readOnly,
  } = useAccountingHospitalContext();

  return (
    <div
      className={cn(
        "space-y-4 p-4 md:p-6",
        inCaPortal &&
          "rounded-xl border border-slate-200 bg-white text-slate-900 shadow-sm"
      )}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-slate-700" />
            <h1 className="text-xl font-semibold tracking-tight text-slate-900">
              Accounting
            </h1>
          </div>
          <p className="mt-1 text-sm text-slate-500">
            Double-entry books. Ops post journals going forward; bank statement import and
            pharmacy COGS are live. Historical totals need a controlled migration.
            {readOnly
              ? " View-only access — you can open every tab; posting and edits are disabled."
              : null}
          </p>
        </div>
        {needsHospitalPicker ? (
          <div className="w-full max-w-xs space-y-1">
            <Label className="text-xs text-slate-500">Hospital</Label>
            <Select value={hospitalId || undefined} onValueChange={setHospitalId}>
              <SelectTrigger>
                <SelectValue placeholder="Select hospital" />
              </SelectTrigger>
              <SelectContent>
                {hospitalOptions.map((h) => (
                  <SelectItem key={h.id} value={h.id}>
                    {h.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : null}
      </div>

      <nav className="flex flex-wrap gap-1 border-b border-slate-200 pb-2">
        {tabs.map((t) => (
          <NavLink
            key={t.to}
            to={t.to}
            end={t.end}
            className={({ isActive }) =>
              `rounded-md px-3 py-1.5 text-sm font-medium transition ${
                isActive
                  ? "bg-slate-900 text-white"
                  : "text-slate-600 hover:bg-slate-100"
              }`
            }
          >
            {t.label}
          </NavLink>
        ))}
      </nav>

      {!hospitalId ? (
        <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          Select a hospital to view books.{" "}
          <Link className="underline" to="..">
            Back
          </Link>
        </p>
      ) : (
        <Outlet />
      )}
    </div>
  );
}

export default function AccountingLayout() {
  return (
    <AccountingHospitalProvider>
      <AccountingShell />
    </AccountingHospitalProvider>
  );
}
